import {
	Component,
	EventEmitter,
	inject,
	Input,
	OnInit,
	Output,
} from '@angular/core';
import { HttpEventType, HttpResponse } from '@angular/common/http';
import { FormsModule, NgForm } from '@angular/forms';
import {
	ItIconComponent,
	ItModalComponent,
	ItSelectComponent,
	ItUploadFileListComponent,
	IconName,
	ItButtonDirective,
	ItProgressButtonComponent,
	UploadFileListItem,
	ItFileUtils,
} from 'design-angular-kit';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IterlexService } from '../iterlex.service';
import { environment } from './../../environments/environment';
import { catchError, finalize, map, of, skipWhile, switchMap, tap } from 'rxjs';
import type { Authority } from '../interfaces/authority';
import type { DocumentComplete } from '../interfaces/document-complete';
import type { Dossier } from '../interfaces/dossier';
import type { File } from '../interfaces/file';
import type { Phase } from '../interfaces/phase';
import type { Step } from '../interfaces/step';
import type { Profile } from '../interfaces/profile';

@Component({
	selector: 'app-files',
	imports: [
		ItIconComponent,
		ItModalComponent,
		ItSelectComponent,
		ItButtonDirective,
		ItProgressButtonComponent,
		ItUploadFileListComponent,
		FormsModule,
		TranslateModule,
	],
	templateUrl: './files.component.html',
	styleUrl: './files.component.scss',
})
export class FilesComponent implements OnInit {
	private translateService = inject(TranslateService);
	private iterlexService = inject(IterlexService);

	@Input({ required: true }) dossier!: Dossier;
	@Input({ required: true }) documentComplete!: DocumentComplete;
	@Input({ required: true }) version!: DocumentComplete['versions'][0];
	@Input({ required: true }) authority!: Authority;
	@Input({ required: true }) body!: Authority;
	@Input({ required: true }) phase!: Phase;
	@Input({ required: true }) step!: Step;
	@Input() showChangeActions = true;
	@Output() updateFileList = new EventEmitter();
	uploadedFileList: UploadFileListItem[] = [];
	progressDeleteFileUri = '';
	profileRoles: Profile['roles'] = [];
	currentFile!: globalThis.File;
	ngOnInit(): void {
		this.iterlexService.getProfile().subscribe((profile) => {
			this.profileRoles = profile.roles.filter(
				(r) =>
					r.name.split('|')[0] == this.authority.id ||
					r.name == 'Admin',
			);
		});
	}
	getDefaultRole() {
		return localStorage.getItem('defaultRole') ?? '';
	}
	getIconForContentType(contentType: string): IconName {
		switch (contentType) {
			case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
				return 'file-docx';
			case 'application/pdf':
				return 'file-pdf-ext';
			case 'message/rfc822':
				return 'mail';
			case 'text/xml':
			case 'application/xml':
			case 'application/akn+xml':
				return 'file-xml';
			default:
				return 'file';
		}
	}
	documentLink(file: File) {
		this.iterlexService
			.downloadFile(
				file.manifestationUri,
				this.authority.id,
				this.body.id,
			)
			.subscribe((blob) => {
				const link = document.createElement('a');
				link.href = URL.createObjectURL(blob);
				link.download = file.title;
				link.click();
			});
	}
	isFileSupportedByReadylex(file: File): boolean {
		return (
			file.contentType === 'application/akn+xml' ||
			file.contentType === 'application/xml' ||
			file.contentType === 'text/xml' ||
			file.contentType ===
				'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
			file.contentType === 'application/msword' ||
			file.contentType === 'application/vnd.oasis.opendocument.text'
		);
	}
	openInReadylexLink(file: File): string {
		const urlParams = new URLSearchParams({
			manifestationUri: file.manifestationUri,
			authority: this.authority.id,
			body: this.body.id,
			phase: this.phase.id,
			step: this.step.id,
			dossierUri: this.dossier.workUri,
		});
		if (this.documentComplete.aliases.length > 1) {
			urlParams.append('workUri', this.documentComplete.workUri);
		}
		return `${environment.readylex_url}/?${urlParams.toString()}`;
	}
	deleteFile(manifestationUri: string) {
		this.progressDeleteFileUri = manifestationUri;
		this.iterlexService
			.deleteFile(manifestationUri, this.authority.id, this.body.id)
			.subscribe({
				next: () => {
					this.updateFileList.emit();
				},
				complete: () => {
					this.progressDeleteFileUri = '';
				},
			});
	}
	setCurrentFile(files: FileList | null) {
		if (files) {
			this.currentFile = files[0];
		}
	}
	lockFile(form: NgForm, manifestationUri: string) {
		if (form.valid) {
			if (form.value.role) {
				localStorage.setItem('defaultRole', form.value.role);
			}
			this.iterlexService
				.lockFile(
					manifestationUri,
					this.authority.id,
					this.body.id,
					form.value.role ?? this.profileRoles[0].id,
				)
				.subscribe(() => this.updateFileList.emit());
		}
	}
	unlockFile(form: NgForm, manifestationUri: string, roleId: string) {
		if (form.valid) {
			if (form.value.file) {
				ItFileUtils.fileToBase64(this.currentFile)
					.pipe(
						switchMap((base64) =>
							this.iterlexService.unlockFile(
								manifestationUri,
								this.authority.id,
								this.body.id,
								roleId,
								this.currentFile.name,
								base64,
							),
						),
					)
					.subscribe(() => this.updateFileList.emit());
			} else {
				this.iterlexService
					.unlockFile(
						manifestationUri,
						this.authority.id,
						this.body.id,
						roleId,
					)
					.subscribe(() => this.updateFileList.emit());
			}
		}
	}
	onDeleteFileList(item: UploadFileListItem): void {
		this.uploadedFileList = this.uploadedFileList.filter(
			(i) => i.id !== item.id,
		);
	}
	onUpdateFileList(
		files: FileList,
		version: DocumentComplete['versions'][0],
	): void {
		this.uploadedFileList = Array.from(files).map((file, index) => ({
			id: index + this.uploadedFileList.length,
			file,
			progress: 1,
			removable: false,
		}));
		const fileToUpload = this.uploadedFileList[0];
		let overwriteFile = false;
		if (
			version.files
				.map((e) => e.manifestationUri)
				.includes(`${version.expressionUri}/!${fileToUpload.file.name}`)
		) {
			if (
				confirm(
					this.translateService.instant(
						'iterlex.overwrite-file-confirm',
					),
				)
			) {
				overwriteFile = true;
			} else {
				this.uploadedFileList = [];
				return;
			}
		}
		ItFileUtils.fileToBase64(fileToUpload.file)
			.pipe(
				switchMap((base64) => {
					if (overwriteFile) {
						return this.iterlexService.editFile(
							version.expressionUri,
							this.authority.id,
							this.body.id,
							fileToUpload.file.name,
							version.date,
							this.body.id,
							base64,
						);
					} else {
						return this.iterlexService.createFile(
							version.expressionUri,
							this.authority.id,
							this.body.id,
							fileToUpload.file.name,
							version.date,
							this.body.id,
							base64,
						);
					}
				}),
				tap((event) => {
					// Update the progress request status
					if (event.type === HttpEventType.UploadProgress) {
						fileToUpload.progress = Math.round(
							(event.loaded / (event.total || 1)) * 100,
						);
					}
				}),
				skipWhile((event) => event.type !== HttpEventType.Response), // skip while the request not is complete
				map((event) => (event as HttpResponse<globalThis.File>).body), // request is complete with body
				tap(() => {
					fileToUpload.progress = 100;
					this.uploadedFileList = [];
					this.updateFileList.emit();
				}),
				catchError((error) => {
					fileToUpload.progress = undefined;
					fileToUpload.removable = true;
					fileToUpload.error = true;
					fileToUpload.tooltip = error.message; // Show error message as tooltip
					return of(null); // File upload failed
				}),
				finalize(() => {
					if (
						!fileToUpload.error &&
						fileToUpload.progress &&
						fileToUpload.progress < 100
					) {
						// There are no errors but the file upload is not complete
						fileToUpload.progress = undefined;
						fileToUpload.removable = true;
						fileToUpload.error = true;
						fileToUpload.tooltip = 'Incomplete upload error'; // Show error message as tooltip
					}
				}),
			)
			.subscribe();
	}
}
